import React, { useState, useEffect } from 'react';
import { useDiscovery } from '../../contexts/DiscoveryContext';
import { gameAnalyticsConfig } from '../../config/gameAnalytics.js';
import ScoreCriteriaManager from './ScoreCriteriaManager';
import { Play, Download } from 'lucide-react';
import { PUBLISHER_CLASSES } from '../../constants/systemConstants.js';

// Static list of tags from the API
const API_TAGS = [
    "Action", "Adventure", "Indie", "RPG", "Strategy", "Simulation", "Casual",
    "Singleplayer", "Early Access", "Free to Play", "2D", "Atmospheric", "3D",
    "Massively Multiplayer", "Multiplayer", "Story Rich", "Fantasy", "Puzzle",
    "Sports", "Violent", "FPS", "Shooter", "Combat", "PvP", "Survival",
    "Open World", "Roguelike", "Horror", "Platformer", "Racing", "Action RPG",
    "Third Person", "First-Person", "Tactical", "Sci-fi", "Military"
].sort();

// Static list of publisher classes from centralized constants
const PUBLISHER_CLASSES_LIST = PUBLISHER_CLASSES.ALL;

const GameDiscoverySettings = () => {
    const {
        settings,
        updateFilters,
        updateScoring,
        updatePublisherClasses,
        triggerDiscovery
    } = useDiscovery();

    const [availableGenres, setAvailableGenres] = useState([]);
    const [isLoadingGenres, setIsLoadingGenres] = useState(false);

    // Load genres on demand when user requests them
    const loadGenres = async () => {
        setIsLoadingGenres(true);
        try {
            const genresResponse = await fetch('/analytics/api/genres');
            const genresData = await genresResponse.json();
            setAvailableGenres(genresData.genres.map(genre => genre.label).sort());
        } catch (error) {
            console.error('Error fetching genres:', error);
        } finally {
            setIsLoadingGenres(false);
        }
    };

    return (
        <div className="space-y-4">
            {/* Run Discovery Button at the top */}
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
                <button
                    onClick={triggerDiscovery}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                    <Play className="w-4 h-4" />
                    Run Discovery
                </button>
            </div>

            <h3 className="text-sm font-medium text-gray-700">Discovery Settings</h3>

            {/* Quick Filters in 2 columns */}
            <div>
                <label className="block text-xs text-gray-600 mb-1">Quick Filters</label>
                <div className="grid grid-cols-2 gap-2">
                    <label className="flex items-center p-2 rounded hover:bg-gray-50">
                        <input
                            type="checkbox"
                            checked={settings.filters.includeEarlyAccess}
                            onChange={(e) => updateFilters({ includeEarlyAccess: e.target.checked })}
                            className="rounded text-blue-600"
                        />
                        <span className="ml-2 text-xs">Early Access</span>
                    </label>
                    <label className="flex items-center p-2 rounded hover:bg-gray-50">
                        <input
                            type="checkbox"
                            checked={settings.filters.includeUnreleased}
                            onChange={(e) => updateFilters({ includeUnreleased: e.target.checked })}
                            className="rounded text-blue-600"
                        />
                        <span className="ml-2 text-xs">Unreleased</span>
                    </label>
                </div>
            </div>

            {/* Minimum Score */}
            <div>
                <label className="block text-xs text-gray-600 mb-1">Minimum Score</label>
                <input
                    type="number"
                    min="1"
                    max="50"
                    value={settings.filters.minScore}
                    onChange={(e) => updateFilters({ minScore: Number(e.target.value) })}
                    className="w-full p-1 text-sm border rounded"
                />
                <span className="text-xs text-gray-500">Combined score from genres and tags must exceed this value</span>
            </div>

            {/* Thresholds Grid */}
            <div className="grid grid-cols-2 gap-4">
                {/* Wishlist Threshold */}
                <div>
                    <label className="block text-xs text-gray-600 mb-1">Min. Wishlists</label>
                    <select
                        value={settings.filters.minWishlist}
                        onChange={(e) => updateFilters({ minWishlist: Number(e.target.value) })}
                        className="w-full p-1 text-sm border rounded"
                    >
                        {gameAnalyticsConfig.dropdownOptions.wishlistThresholds.map(option => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Followers Threshold */}
                <div>
                    <label className="block text-xs text-gray-600 mb-1">Min. Followers</label>
                    <select
                        value={settings.filters.minFollowers}
                        onChange={(e) => updateFilters({ minFollowers: Number(e.target.value) })}
                        className="w-full p-1 text-sm border rounded"
                    >
                        {gameAnalyticsConfig.dropdownOptions.followerThresholds.map(option => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Publisher Classes */}
            <div>
                <div className="flex justify-between items-center mb-1">
                    <label className="text-xs text-gray-600">Publisher Classes</label>
                    <button
                        onClick={() => updatePublisherClasses(PUBLISHER_CLASSES_LIST)}
                        className="text-xs text-blue-600 hover:text-blue-700"
                    >
                        Select All
                    </button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                    {PUBLISHER_CLASSES_LIST.map(publisherClass => (
                        <label
                            key={publisherClass}
                            className={`flex items-center p-2 rounded hover:bg-gray-50 ${
                                settings.filters.publisherClasses.length === 1 &&
                                settings.filters.publisherClasses.includes(publisherClass)
                                    ? 'opacity-50 cursor-not-allowed'
                                    : ''
                            }`}
                        >
                            <input
                                type="checkbox"
                                checked={settings.filters.publisherClasses.includes(publisherClass)}
                                onChange={(e) => {
                                    // If trying to uncheck the last selected class, prevent the action
                                    if (!e.target.checked && settings.filters.publisherClasses.length === 1) {
                                        return;
                                    }
                                    const newClasses = e.target.checked
                                        ? [...settings.filters.publisherClasses, publisherClass]
                                        : settings.filters.publisherClasses.filter(c => c !== publisherClass);
                                    updatePublisherClasses(newClasses);
                                }}
                                className="rounded text-blue-600"
                                disabled={settings.filters.publisherClasses.length === 1 &&
                                         settings.filters.publisherClasses.includes(publisherClass)}
                            />
                            <span className="ml-2 text-xs">
                                {publisherClass}
                            </span>
                        </label>
                    ))}
                </div>
            </div>

            {/* Scoring Managers */}
            <div className="space-y-2">
                <ScoreCriteriaManager
                    title="Genre Scoring"
                    type="genre"
                    currentScores={settings.scoring.genres}
                    availableCriteria={availableGenres}
                    onUpdate={(newGenres) => updateScoring({
                        ...settings.scoring,
                        genres: newGenres
                    })}
                    onRequestData={availableGenres.length === 0 ? loadGenres : null}
                    isLoading={isLoadingGenres}
                />
                <ScoreCriteriaManager
                    title="Tag Scoring"
                    type="tag"
                    currentScores={settings.scoring.tags}
                    availableCriteria={API_TAGS}
                    onUpdate={(newTags) => updateScoring({
                        ...settings.scoring,
                        tags: newTags
                    })}
                />
            </div>
        </div>
    );
};

export default GameDiscoverySettings;
